import { useQuery } from '@tanstack/react-query';

import { fromSeed } from '../character/config';
import { api } from '../../lib/api';
import { StageScene } from '../rooms/stage/StageScene';

/**
 * The half of an auth screen that is not a form.
 *
 * ## It is the venue, not a gradient and not a lineup
 *
 * Every music service's sign-in page is a mesh gradient with a waveform over
 * it, and they are interchangeable because nothing in them is about the product
 * behind them. This one draws the actual room: the truss, the lights, the
 * screen, the booth, a host behind the decks and a crowd in front of them. It
 * is the thing the platform is, before anybody has read a word of the copy.
 *
 * It replaced a row of characters standing on a colour wash. That was already
 * ours rather than stock, and the stage is strictly more of the same idea - the
 * characters are still here, they are just in a room now instead of on a shelf.
 *
 * ## It costs nothing to ship
 *
 * `StageScene` and its decor are in the main bundle already, because there is
 * no route splitting: every visitor downloads the venue whether or not they
 * ever open a room. Drawing it here spends bytes that were already spent.
 *
 * ## Seeded, so it is the same room every time
 *
 * `fromSeed` is deterministic and the seeds are literals, computed once at
 * module scope. A random crowd would redraw on every keystroke that re-rendered
 * the page - a sign-in screen that flickers while you type your password - and
 * nobody could ever recognise the place twice.
 *
 * ## And it wears whatever the platform is wearing
 *
 * The theme comes from the same setting the live rooms read, so the sign-in
 * page is decorated for Halloween on the day an administrator decorates
 * everything else. This is the one screen seen by people who do not have
 * accounts, which makes it the one where seasonal dressing does the most work,
 * and it needed a public endpoint to get there - see `CurrentStageThemeAction`.
 *
 * It degrades to the default theme on a failed or slow request. A sign-in page
 * must not wait on decoration.
 *
 * ## Fitted by width and stood on the floor, not cropped to fill
 *
 * The scene is 16:9 and this panel is a tall column, so something has to give.
 * Cropping to fill was the first attempt and it is wrong in the same way it was
 * wrong for the room, in the other direction: filling the height of a panel
 * this narrow shows about sixty per cent of the width, which is a close-up of
 * the screen and two enormous strangers.
 *
 * So it fits by width and sits on the bottom edge, and the parts of the scene
 * with no perspective in them cover the rest: the backdrop is stretched over
 * the whole panel and the painted crowd tiles sideways. `StageDeck` makes the
 * same argument about a section that is too wide - see the note there.
 *
 * A third wider than the panel rather than exactly its width, which is the
 * whole of the tuning. At exactly the width the venue is complete and small,
 * with a third of the panel empty above it; any larger and the speaker stacks
 * go off the sides and it stops reading as a room. The bit that goes off the
 * edges at this size is truss and stacks, which repeat - not the screen, and
 * not anybody's face.
 *
 * ## The bars move on their own
 *
 * Nothing is driving `--eq` here, and `.eq` animates by default when nothing
 * is - see `stage.css`. So the booth and the screen have life in them without
 * this page knowing anything about a track.
 *
 * ## It is decoration, and says so
 *
 * `aria-hidden`, and nothing in it is focusable. Everything a screen reader
 * needs is in the column beside it.
 */

/**
 * Who is on stage and who is watching.
 *
 * Eleven, which is what the scene has places for. Fewer would leave the painted
 * blobs showing through gaps in the front row, which is right in a half-empty
 * room and wrong on a page whose job is to look like a good night.
 */
/**
 * The venue's size in the panel, in both directions at once.
 *
 * A third wider than the panel, unless that would make it taller than the
 * panel - which happens on a wide, short window, where a width-driven box would
 * push the screen and the truss off the top and leave a close-up of somebody's
 * shoulders. `StageDeck` uses the same `min()` pair against the other
 * constraint; the container query units are why the panel declares
 * `container-type: size`.
 */
const BOX = {
  width: 'min(132cqw, calc(100cqh * 16 / 9))',
  height: 'min(100cqh, calc(132cqw * 9 / 16))',

  /*
   * And its top edge dissolved into the backdrop.
   *
   * The scene has a faint ground of its own behind the truss, which against the
   * stretched backdrop above it reads as a horizontal step across the panel -
   * a seam where the drawing starts. Fading the first few per cent removes it
   * and costs only the very top of the truss, which is a repeating rail.
   */
  maskImage: 'linear-gradient(to bottom, transparent 0, black 9%)',
  WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, black 9%)',
} as const;

const HOST = fromSeed('auth-host');

const CROWD = [
  'auth-fan-1', 'auth-fan-2', 'auth-fan-3', 'auth-fan-4', 'auth-fan-5', 'auth-fan-6',
  'auth-fan-7', 'auth-fan-8', 'auth-fan-9', 'auth-fan-10', 'auth-fan-11',
].map((seed) => ({ config: fromSeed(seed) }));

export function AuthStage({ headline, pitch }: { headline: string; pitch: string }) {
  /*
   * Read anonymously and never retried. The panel is decoration, and a sign-in
   * page that retries a decoration request is a sign-in page spending somebody's
   * connection on a picture.
   */
  const { data } = useQuery({
    queryKey: ['stage', 'theme'],
    queryFn: () => api.get<{ theme: string }>('/stage-theme', { anonymous: true }),
    staleTime: Infinity,
    retry: false,
  });

  const theme = data?.theme ?? 'default';

  /* The class the decor and the palette hang off - see `stage.css`. */
  const themeClass = theme === 'default' ? '' : `stage-${theme}`;

  return (
    <aside
      data-auth-stage
      data-live-stage
      data-stage-theme={theme}
      aria-hidden
      /*
        Stuck to the top at exactly one screen tall.

        The register form is taller than a laptop window, and a stage that grew
        with the grid put the crowd's feet below the fold - so the picture was
        only ever complete after scrolling past the thing somebody came to fill
        in. Sticky at `100dvh` means it is always a whole composition and the
        form scrolls beside it.
      */
      className="relative hidden overflow-hidden lg:sticky lg:top-0 lg:block lg:h-[100dvh]"
      // `container-type: size` so the box below can be sized against this panel
      // in both directions at once - see the note on it.
      style={{ background: '#0d0620', containerType: 'size' }}
    >
      {/*
        The backdrop, stretched over the whole panel rather than over the stage.

        It is the one part of the drawing with no perspective in it, so it can
        be any shape - and it has to be, because it is what fills the height
        above the truss on a panel far taller than 16:9. Outside the box below
        for the same reason `StageDeck` puts it outside `LiveStage`: anything
        inside is measured against the stage, and this needs to be bigger than
        the stage.
      */}
      <div className={`absolute inset-0 ${themeClass}`} data-stage-sky>
        <StageScene layer="sky" host={HOST} crowd={[]} theme={theme} />
      </div>

      {/*
        Light in the ceiling.

        The backdrop is a dark wash by design - it sits behind a lit stage in
        the room, where the truss fills the top of the frame. Here the panel is
        far taller than the scene, so above the truss there is a large quiet
        area that reads as switched off rather than as a room with height.

        These are the washes the old crowd panel stood on, kept for that reason
        and turned down: three radials at different sizes, in the colours the
        platform already uses for signal, heat and go, overlapping unevenly. A
        single corner-to-corner gradient is the thing that makes these pages
        interchangeable, and this is deliberately not one.
      */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-2/3"
        style={{
          backgroundImage: [
            'radial-gradient(62% 58% at 12% 0%, rgb(123 132 242 / 0.5), transparent 72%)',
            'radial-gradient(48% 46% at 92% 6%, rgb(255 63 74 / 0.36), transparent 70%)',
            'radial-gradient(44% 44% at 62% 0%, rgb(232 84 166 / 0.28), transparent 70%)',
          ].join(', '),
        }}
      />

      {/*
        The venue, as wide as the panel and standing on its floor.

        Bottom-anchored rather than centred: the crowd's feet are the bottom
        edge of the scene, and a gap under them would leave the room floating
        in its own sky.

        The layer order is the scene's own, and the same one `LiveStage` uses,
        minus the screen overlay - nothing is playing here, so the glass stays
        as the drawing has it.
      */}
      <div
        className={`absolute bottom-0 left-1/2 -translate-x-1/2 ${themeClass}`}
        style={BOX}
      >
        <StageScene layer="back" host={HOST} crowd={CROWD} theme={theme} />

        <div className="absolute inset-0">
          <StageScene layer="front" host={HOST} crowd={CROWD} theme={theme} />

          {/* Three stage-widths of painted crowd, centred on this one, so the
              floor carries out past the panel's edges. */}
          <div className="absolute inset-y-0 -left-full flex w-[300%]" data-stage-audience>
            {[0, 1, 2].map((tile) => (
              <div key={tile} className="h-full w-1/3">
                <StageScene layer="audience" host={HOST} crowd={[]} theme={theme} />
              </div>
            ))}
          </div>

          <div className="absolute inset-0">
            <StageScene layer="fans" host={HOST} crowd={CROWD} theme={theme} />
          </div>
        </div>
      </div>

      {/*
        A scrim under the words, and only under the words.

        The headline sits over the truss and the house lights, which are the
        brightest part of the drawing and the least predictable - a theme can
        change what is up there. A wash across the top third means the text has
        a floor to stand on whatever the venue is wearing tonight, without
        putting a panel over the picture.
      */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-1/2"
        style={{ backgroundImage: 'linear-gradient(to bottom, rgb(8 4 20 / 0.78), rgb(8 4 20 / 0.35) 45%, transparent)' }}
      />

      {/*
        And one along the bottom, which is doing something different: it seats
        the crowd on the panel's edge rather than letting the drawing stop in a
        line across it.
      */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1/4"
        style={{ backgroundImage: 'linear-gradient(to top, rgb(8 4 20 / 0.55), transparent)' }}
      />

      <div className="relative flex h-full flex-col justify-start p-12 xl:p-16">
        {/*
          Explicit white rather than `text-ink`. This is sitting on artwork, so
          the contrast it needs is against a lit stage, not against whatever the
          page's surface happens to be in this theme.
        */}
        <h2 className="max-w-[14ch] text-pretty text-[2.75rem] font-semibold leading-[1.05] tracking-tight text-white xl:text-[3.25rem]">
          {headline}
        </h2>

        <p className="mt-5 max-w-[46ch] text-pretty text-[15px] leading-relaxed text-white/70">{pitch}</p>
      </div>
    </aside>
  );
}
